import { useState } from 'react';
import { Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Actions, Block, DangerLink, DetailModal, FactsBlock, Field, GhostBtn, Muted, Select, StatusPill } from '../../components';
import { assigneeName, useUpdateBookingMutation } from '../../queries/scheduling';
import { formatPhone, toE164 } from '../../utils/callLog';
import { STATUS, TYPE_LABEL } from './constants';
import { DEFAULT_TZ, longDateIn } from './time';

const durationMin = (b) => Math.round((new Date(b.end) - new Date(b.start)) / 60000);

function BookingFacts({ booking, tz }) {
  const phone = toE164(booking.contact?.phone);
  const status = STATUS[booking.status] || STATUS.booked;
  const facts = [
    { label: 'Status', value: <StatusPill color={status.color}>{status.label}</StatusPill> },
    { label: 'When', value: longDateIn(booking.start, tz) },
    { label: 'Length', value: `${durationMin(booking)} min` },
    {
      label: 'Phone',
      value: booking.contact?.phone && (phone ? <a href={`tel:${phone}`}>{formatPhone(booking.contact.phone)}</a> : booking.contact.phone)
    },
    { label: 'Booked by', value: booking.source === 'ai_call' ? 'Assistant' : 'Team' },
    { label: 'Notes', value: booking.notes }
  ];
  return <FactsBlock facts={facts} />;
}

function AssigneeField({ booking, assignees, disabled, onAssign }) {
  return (
    <Block>
      <h3>
        <label htmlFor="booking-agent">Agent</label>
      </h3>
      {disabled && !booking.assignedTo ? (
        <Muted>No agent assigned.</Muted>
      ) : (
        <Select
          id="booking-agent"
          style={{ width: '100%' }}
          disabled={disabled}
          value={booking.assignedTo?._id || ''}
          onChange={(e) => onAssign(assignees.find((a) => a._id === e.target.value) || null)}
        >
          <option value="">Unassigned</option>
          {assignees.map((a) => (
            <option key={a._id} value={a._id}>
              {assigneeName(a)}
            </option>
          ))}
        </Select>
      )}
    </Block>
  );
}

function Reschedule({ booking, tz, onSave }) {
  const zone = tz || DEFAULT_TZ;
  const current = dayjs(booking.start).tz(zone).format('YYYY-MM-DDTHH:mm');
  const [value, setValue] = useState(current);
  return (
    <Block>
      <h3>
        <label htmlFor="booking-reschedule">Reschedule</label>
      </h3>
      <div style={{ display: 'flex', gap: 8 }}>
        <Field id="booking-reschedule" type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
        <GhostBtn type="button" disabled={!value || value === current} onClick={() => onSave(dayjs.tz(value, zone).toISOString())}>
          Move
        </GhostBtn>
      </div>
    </Block>
  );
}

export default function BookingModal({ booking, assignees, tz, canManage, onClose }) {
  const update = useUpdateBookingMutation();
  const change = (changes) => update.mutate({ id: booking._id, changes });
  const open = booking.status === 'booked';

  return (
    <DetailModal
      width={480}
      title={booking.type === 'block' ? booking.notes || 'Blocked time' : booking.contact?.name || 'Unnamed contact'}
      subtitle={
        <>
          {TYPE_LABEL[booking.type] || 'Tour'} · {longDateIn(booking.start, tz)}
        </>
      }
      onClose={onClose}
      footer={
        canManage && open ? (
          <Actions>
            <GhostBtn type="button" onClick={() => change({ status: 'completed' })}>
              <FontAwesomeIcon icon={faCheck} />
              Mark completed
            </GhostBtn>
            <Popconfirm
              title="Cancel this booking?"
              description="The time slot opens up again."
              okText="Cancel booking"
              cancelText="Keep it"
              okButtonProps={{ danger: true }}
              onConfirm={() => change({ status: 'cancelled' })}
            >
              <DangerLink type="button">Cancel booking</DangerLink>
            </Popconfirm>
          </Actions>
        ) : null
      }
    >
      <BookingFacts booking={booking} tz={tz} />
      <AssigneeField
        booking={booking}
        assignees={assignees}
        disabled={!canManage}
        onAssign={(assignee) => change({ assignedTo: assignee })}
      />
      {canManage && open && <Reschedule key={booking.start} booking={booking} tz={tz} onSave={(start) => change({ start })} />}
    </DetailModal>
  );
}
